/*
  NEO Sabers — Auto gifts (replaces EG Auto Add to Cart)
  Used by sections/neo-auto-gifts.liquid (loaded by the section).

  Why it doesn't clash under load:
  - ONE sync at a time (single-flight). Requests that come in while a sync runs are
    merged into ONE follow-up run.
  - Each sync reads /cart.js, works out the wanted state, and only applies the difference:
    max one /cart/update.js + one /cart/add.js per run.
  - Loop protection: max 3 back-to-back runs; a failed add (e.g. out of stock) is not
    retried for 60s.
  - Our own requests use the native fetch, so they never re-trigger the watcher.
*/
(() => {
  if (window.__neoAutoGifts) return;
  window.__neoAutoGifts = true;

  const PROP = '_neo_auto';
  const OTHER_GIFT_PROPS = ['_neo_auto', '_neo_gift', '_neo_offer'];
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const nativeFetch = window.__neoNativeFetch || (window.__neoNativeFetch = window.fetch.bind(window));
  const SEEN_KEY = 'neoAgSeen';
  const DISMISS_KEY = 'neoAgDismissed';

  let cfg = null;
  let root = null;
  const idSets = {};          // collection handle -> Set(product ids)
  const failedUntil = {};     // variant id -> timestamp
  let running = false;
  let again = false;
  let chain = 0;
  let timer;

  /* ---------------- helpers ---------------- */
  const api = async (path, body) => {
    const res = await nativeFetch(ROOT + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(json.description || json.message || String(res.status));
      err.status = res.status;
      throw err;
    }
    return json;
  };

  const store = {
    get(k) { try { return JSON.parse(sessionStorage.getItem(k)) || {}; } catch (e) { return {}; } },
    set(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  };

  const isAnyGift = (item) =>
    item.properties && OTHER_GIFT_PROPS.some((p) => item.properties[p]);

  const loadIds = async (handle) => {
    if (!handle || idSets[handle]) return idSets[handle];
    const key = 'neoAgIds:' + handle;
    const c = store.get(key);
    if (c.t && Date.now() - c.t < 30 * 60 * 1000) return (idSets[handle] = new Set(c.ids));
    const res = await nativeFetch(`${ROOT}collections/${handle}/products.json?limit=250`);
    const json = await res.json();
    idSets[handle] = new Set((json.products || []).map((p) => p.id));
    store.set(key, { t: Date.now(), ids: [...idSets[handle]] });
    return idSets[handle];
  };

  const refreshDrawer = (cart) => {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } })
    );
  };

  /* ---------------- wanted quantity per rule ---------------- */
  const wantedQty = (rule, cart) => {
    if (rule.enabled === false) return 0; // rule switched off -> remove its gift
    const paid = cart.items.filter((i) => !isAnyGift(i) && i.final_line_price > 0);

    if (rule.type === 'cart_total') {
      const subtotal = paid.reduce((s, i) => s + i.final_line_price, 0);
      return subtotal >= rule.threshold ? rule.qty : 0;
    }

    const ids = idSets[rule.collection];
    if (!ids) return null; // not loaded -> don't touch
    const qual = paid.filter((i) => ids.has(i.product_id)).reduce((s, i) => s + i.quantity, 0);
    if (!qual) return 0;
    let want = rule.perQty ? rule.qty * qual : rule.qty;
    if (rule.maxQty > 0) want = Math.min(want, rule.maxQty);
    return want;
  };

  /* ---------------- sync ---------------- */
  const sync = async () => {
    if (!cfg) return;
    if (running) { again = true; return; }
    running = true;

    const added = [];
    try {
      const cart = await api('cart.js');
      const seen = store.get(SEEN_KEY);
      const dismissed = store.get(DISMISS_KEY);
      const updates = {};
      const adds = [];

      cfg.rules.forEach((rule) => {
        let want = wantedQty(rule, cart);
        if (want === null) return;

        const lines = cart.items.filter((i) => i.properties && i.properties[PROP] === rule.key);
        const have = lines.filter((l) => l.variant_id === rule.variant).reduce((s, l) => s + l.quantity, 0);

        // customer removed it themselves -> remember (only if setting is on)
        if (cfg.respectRemoval) {
          if (want === 0) delete dismissed[rule.key];
          else if (seen[rule.key] && have === 0) dismissed[rule.key] = true;
          if (dismissed[rule.key]) want = 0;
        }

        // fix lines: wrong variant -> 0, right variant -> keep up to `want`
        let left = want;
        lines.forEach((l) => {
          const keep = l.variant_id === rule.variant ? Math.min(l.quantity, left) : 0;
          left -= keep;
          if (keep !== l.quantity) updates[l.key] = keep;
        });

        if (left > 0 && !(failedUntil[rule.variant] > Date.now())) {
          adds.push({ rule, qty: left });
        }

        seen[rule.key] = want > 0 && have > 0;
      });

      let changed = false;

      if (Object.keys(updates).length) {
        await api('cart/update.js', { updates });
        changed = true;
      }

      if (adds.length) {
        try {
          await api('cart/add.js', {
            items: adds.map(({ rule, qty }) => ({
              id: rule.variant,
              quantity: qty,
              properties: { [PROP]: rule.key }
            }))
          });
          added.push(...adds.map((a) => a.rule));
          adds.forEach((a) => (seen[a.rule.key] = true));
          changed = true;
        } catch (err) {
          // one bad item fails the whole request: retry one by one, skip the failing ones
          for (const { rule, qty } of adds) {
            try {
              await api('cart/add.js', { items: [{ id: rule.variant, quantity: qty, properties: { [PROP]: rule.key } }] });
              added.push(rule);
              seen[rule.key] = true;
              changed = true;
            } catch (e) {
              failedUntil[rule.variant] = Date.now() + 60 * 1000;
              console.warn('[neo-auto-gifts] could not add', rule.key, e.message);
            }
          }
        }
      }

      store.set(SEEN_KEY, seen);
      store.set(DISMISS_KEY, dismissed);

      if (changed) {
        const fresh = await api('cart.js');
        refreshDrawer(fresh);
        chain += 1;
        if (chain < 3) again = true; // one check run to confirm state settled
      } else {
        chain = 0;
      }
    } catch (e) {
      console.warn('[neo-auto-gifts]', e.message);
    } finally {
      running = false;
      if (added.length) showNotes(added);
      if (again) {
        again = false;
        setTimeout(sync, 300);
      } else {
        chain = 0;
      }
    }
  };

  const queue = (delay = 350) => {
    clearTimeout(timer);
    timer = setTimeout(sync, delay);
  };

  /* ---------------- notifications ---------------- */
  const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const hideNote = (note) => {
    note.classList.remove('is-show');
    setTimeout(() => note.remove(), 400);
  };

  const showNotes = (rules) => {
    if (!cfg.toast || !root) return;
    const stack = root.querySelector('[data-neo-ag-stack]');
    const icon = root.querySelector('[data-neo-ag-gift-icon]');

    rules
      .filter((rule) => rule.notify !== false && rule.notifyTitle)
      .forEach((rule, i) => {
        const note = document.createElement('div');
        note.className = 'neo-ag-note';
        note.setAttribute('role', 'status');
        const iconHtml =
          rule.notifyIcon === 'product' && rule.image
            ? `<img src="${esc(rule.image)}" alt="" width="64" height="64">`
            : icon ? icon.innerHTML : '';
        note.innerHTML = `
          <div class="neo-ag-note__icon">${iconHtml}</div>
          <div>
            <p class="neo-ag-note__title">${esc(rule.notifyTitle)}</p>
            ${rule.notifyText ? `<p class="neo-ag-note__text">${esc(rule.notifyText)}</p>` : ''}
          </div>`;
        note.addEventListener('click', () => hideNote(note));
        stack.appendChild(note);

        setTimeout(() => note.classList.add('is-show'), 30 + i * 180);
        setTimeout(() => hideNote(note), cfg.toastSeconds * 1000 + i * 180);
      });
  };

  /* ---------------- watch cart changes ---------------- */
  // Any theme/app cart request (not ours, ours use nativeFetch) -> queue a sync
  const CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const prevFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = input instanceof Request ? input.url : String(input);
    const p = prevFetch.call(this, input, init);
    if (CART_RE.test(url)) p.then((r) => r.ok && queue()).catch(() => {});
    return p;
  };

  ['cart:change', 'cart:refresh'].forEach((ev) =>
    document.addEventListener(ev, (e) => {
      // our own refresh -> ignore
      if (running) return;
      queue();
    })
  );

  // Coming back to the tab (cart may have changed in another tab)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') queue(100);
  });

  /* ---------------- init ---------------- */
  const init = async () => {
    root = document.querySelector('[data-neo-auto-gifts]');
    if (!root) return;
    try {
      cfg = JSON.parse(root.querySelector('[data-neo-ag-config]').textContent);
    } catch (e) {
      return console.warn('[neo-auto-gifts] bad config');
    }
    if (root.parentElement !== document.body) document.body.appendChild(root);

    const handles = [...new Set(cfg.rules.filter((r) => r.type === 'collection').map((r) => r.collection))];
    await Promise.all(handles.map((h) => loadIds(h).catch(() => null)));
    queue(0);
  };

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('body > [data-neo-auto-gifts]').forEach((el, i, all) => {
      if (all.length > 1 && i === 0) el.remove();
    });
    init();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
